import React from 'react';
import { ChevronLeft, ChevronRight, SkipForward, Wrench } from 'lucide-react';
import { Button } from '../ui/button';

const badge = (r) => {
  if (r === 'OK') return <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-bold">OK</span>;
  if (r === 'NOK') return <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold">NOK</span>;
  return <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-500 text-xs">—</span>;
};

/** Vista "un campione alla volta": pensata per tablet e cellulare in officina. */
export const SingleSampleView = ({
  sample, sampleIndex, samples, planRows, values, measurementsMap, locked,
  onChange, onBlur, onKeyDown, onSelectSample, onNextEmpty, onPendingCollaudo, onIdentifierChange,
  cellRefs,
}) => {
  if (!sample) return null;
  const filled = planRows.filter(r => values[`${sample.id}::${r.id}`]).length;

  return (
    <div className="space-y-3" data-testid="qcr-single-sample-view">
      {/* Sample switcher */}
      <div className="sticky top-16 z-20 bg-white border rounded-xl p-3 shadow-sm">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-12 w-12 p-0 shrink-0"
            disabled={sampleIndex === 0}
            onClick={() => onSelectSample(sampleIndex - 1)}
            data-testid="qcr-single-prev-sample"
            aria-label="Campione precedente"
          ><ChevronLeft className="w-6 h-6" /></Button>

          <div className="flex-1 text-center">
            <div className="text-xs text-slate-500">Campione {sampleIndex + 1} di {samples.length}</div>
            <div className="text-2xl font-bold tracking-tight" data-testid="qcr-single-sample-number">
              C{String(sample.sample_number).padStart(2, '0')}
            </div>
            <input
              type="text"
              defaultValue={sample.sample_identifier || ''}
              key={sample.id}
              disabled={locked}
              onBlur={e => { if ((e.target.value || '') !== (sample.sample_identifier || '')) onIdentifierChange(sample, e.target.value); }}
              placeholder="marcatura pezzo"
              className="mt-1 w-full max-w-[220px] mx-auto text-center text-sm h-9 px-2 rounded border border-slate-300"
              data-testid={`qcr-single-ident-${sample.sample_number}`}
            />
          </div>

          <Button
            variant="outline"
            className="h-12 w-12 p-0 shrink-0"
            disabled={sampleIndex >= samples.length - 1}
            onClick={() => onSelectSample(sampleIndex + 1)}
            data-testid="qcr-single-next-sample"
            aria-label="Campione successivo"
          ><ChevronRight className="w-6 h-6" /></Button>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="text-xs text-slate-600">
            Quote compilate <b>{filled}/{planRows.length}</b> · esito {badge(sample.sample_result)}
          </div>
          <Button variant="outline" className="h-10" onClick={onNextEmpty} data-testid="qcr-single-next-empty">
            <SkipForward className="w-4 h-4 mr-1" />Prossima vuota
          </Button>
        </div>
        <div className="mt-2 h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
          <div className="h-full bg-indigo-600 transition-all" style={{ width: `${planRows.length ? (filled / planRows.length) * 100 : 0}%` }} />
        </div>
      </div>

      {/* Characteristics list */}
      <div className="space-y-2">
        {planRows.map((row, ri) => {
          const k = `${sample.id}::${row.id}`;
          const saved = measurementsMap[k];
          const res = saved?.result;
          const border = res === 'OK' ? 'border-emerald-400' : res === 'NOK' ? 'border-red-400' : 'border-slate-200';
          return (
            <div key={row.id} className={`bg-white border-2 ${border} rounded-xl p-3`} data-testid={`qcr-single-row-${row.numero_caratteristica}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 font-mono">Quota {row.numero_caratteristica}{!row.obbligatorio && ' (non obbligatoria)'}</div>
                  <div className="font-semibold text-slate-900 leading-tight">{row.descrizione_caratteristica}</div>
                  <div className="text-xs text-slate-600 mt-0.5 font-mono">
                    {row.specifica_nominale || '—'} {row.unita_misura || ''}
                    {(row.tolleranza_min || row.tolleranza_max) && ` [${row.tolleranza_min ?? '—'} ; ${row.tolleranza_max ?? '—'}]`}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {badge(res)}
                  {res === 'NOK' && saved?.id && (
                    <Button variant="outline" className="h-10 w-10 p-0 text-orange-700" onClick={() => onPendingCollaudo(saved, row, sample)} data-testid={`qcr-single-pc-${row.numero_caratteristica}`} aria-label="Crea Pending Collaudo">
                      <Wrench className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <input
                ref={el => { cellRefs.current[`${ri}:single`] = el; }}
                type="text"
                inputMode={(row.tipo_valutazione || '').toLowerCase().includes('numerica') ? 'decimal' : 'text'}
                value={values[k] ?? ''}
                disabled={locked}
                onChange={e => onChange(sample.id, row, e.target.value)}
                onBlur={() => onBlur(sample.id, row)}
                onKeyDown={e => onKeyDown(e, ri, row, sample)}
                placeholder="valore misurato"
                className="mt-2 w-full h-14 text-center text-2xl font-mono rounded-lg border-2 border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 disabled:bg-slate-100"
                data-testid={`qcr-cell-${sample.sample_number}-${row.numero_caratteristica}`}
              />
            </div>
          );
        })}
      </div>

      {/* Bottom sample switcher for long lists */}
      <div className="flex items-center justify-between gap-2 pb-2">
        <Button variant="outline" className="h-12 flex-1" disabled={sampleIndex === 0} onClick={() => onSelectSample(sampleIndex - 1)} data-testid="qcr-single-prev-sample-bottom">
          <ChevronLeft className="w-5 h-5 mr-1" />Campione prec.
        </Button>
        <Button variant="outline" className="h-12 flex-1" disabled={sampleIndex >= samples.length - 1} onClick={() => onSelectSample(sampleIndex + 1)} data-testid="qcr-single-next-sample-bottom">
          Campione succ.<ChevronRight className="w-5 h-5 ml-1" />
        </Button>
      </div>
    </div>
  );
};
